import API from '../../Config/Api';

/** The customer agent's doors (stylon-server customer-agent module). */
export async function sendAgentMessage(slug, body) {
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/agent/chat`, body);
  return data;
}

export async function decideAgentProposal(slug, proposalId, decision, language) {
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/agent/proposals/${encodeURIComponent(proposalId)}/${decision}`, { language });
  return data;
}

/** The signed visitor key, asked for when the chat opens so voice works before any question is typed. */
export async function mintAgentVisitorKey(slug) {
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/agent/visitor`, {});
  return data;
}

/**
 * A recorded question as { text, language } (POST public/salons/:slug/agent/transcribe, only where the agent is on). The visitor
 * key rides in the form, not the address; no language is sent — the server forces none and names the one it heard.
 */
export async function transcribeAgentVoice(slug, blob, { visitorKey } = {}) {
  const form = new FormData();
  form.append('file', blob, blob.name || `voice.${String(blob.type || '').includes('mp4') ? 'mp4' : 'webm'}`);
  if (visitorKey) form.append('visitorKey', visitorKey);
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/agent/transcribe`, form, { timeout: 45000 });
  return data;
}

/**
 * A reply the chat gave this visitor, spoken (POST public/salons/:slug/agent/synthesize). The server speaks ONLY the reply
 * named by `replyId` — the id the chat's answer carried — never text we send. Streams raw PCM16 (24 kHz) as it is made, so
 * this is a fetch (axios cannot hand over a body stream); the base address is the store's own.
 */
export function synthesizeAgentReply(slug, { replyId, visitorKey, signal, part } = {}) {
  const base = String(API.defaults.baseURL || '/v1').replace(/\/$/, '');
  const token = (() => { try { return localStorage.getItem('accessToken') || localStorage.getItem('authToken') || ''; } catch (_) { return ''; } })();
  return fetch(`${base}/public/salons/${encodeURIComponent(slug)}/agent/synthesize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ replyId, format: 'pcm', ...(Number.isInteger(part) ? { part } : {}), ...(visitorKey ? { visitorKey } : {}) }),
    signal,
  });
}

/** A thumb under one of the agent's replies. The server works out whose conversation it is; we only name the reply. */
export async function sendAgentFeedback(slug, body) {
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/agent/feedback`, body);
  return data;
}

/** The shop's own search by meaning: ids in order, with the storefront's card facts. */
export async function searchShopByMeaning(slug, q, opts = {}) {
  const { data } = await API.get(`/public/salons/${encodeURIComponent(slug)}/product-search`, { params: { q, ...opts } });
  return data;
}

/** "Something like this": the shop searched by a picture. */
export async function searchShopByImage(slug, image, q) {
  const { data } = await API.post(`/public/salons/${encodeURIComponent(slug)}/product-search/image`, { image, ...(q ? { q } : {}) });
  return data;
}

/**
 * A picture the shopper picked, made small enough to send (≤ 1280 px, JPEG, well under 2 MB) — the server takes a
 * data URL up to 2 MB.
 */
export function shrinkImage(file, max = 1280) {
  return new Promise((resolve, reject) => {
    if (!file || !/^image\//.test(file.type)) { reject(new Error('not an image')); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('unreadable'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('unreadable'));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
